import { useNavigate } from 'react-router-dom';
import { alpha, Box, SxProps, Theme, keyframes, useTheme } from '@mui/material';
import { CardContent } from './ReportSample.styles';
import { forwardRef, useMemo } from 'react';

export const CardWrapper = forwardRef<
  HTMLDivElement,
  {
    isSelected: boolean;
    children: React.ReactNode;
    isEditing: boolean;
    to: string;
    isDragOver?: boolean;
    isDragging?: boolean;
    sx?: SxProps<Theme>;
    onContextMenu?: (event: React.MouseEvent) => void;
  }
>(({ isSelected, children, isEditing, to, isDragOver, isDragging, sx, onContextMenu }, reference) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const pulseBorder = useMemo(
    () => keyframes`
    0%, 100% {
      border-color: ${alpha(theme.palette.secondary.main, 0.6)};
      background-color: ${alpha(theme.palette.secondary.main, 0.08)};
    }
    50% {
      border-color: ${theme.palette.secondary.main};
      background-color: ${alpha(theme.palette.secondary.main, 0.15)};
    }
  `,
    [theme.palette.secondary.main]
  );

  return (
    <Box
      ref={reference}
      onClick={() => {
        if (!isEditing) {
          navigate(to);
        }
      }}
      onContextMenu={onContextMenu}
      sx={{
        flex: 1,
        minWidth: 0,
        cursor: isEditing ? 'default' : 'pointer',
        paddingTop: '2px',
        paddingBottom: '2px',
        ...sx,
      }}
    >
      <CardContent
        selected={isSelected}
        sx={{
          transition: 'all 0.2s ease',
          ...(isDragOver && {
            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            border: `2px solid ${theme.palette.secondary.main}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
            margin: '-1px',
          }),
          ...(isDragging &&
            !isDragOver && {
              animation: `${pulseBorder} 1.5s ease-in-out infinite`,
              borderWidth: '2px',
              borderStyle: 'dashed',
              margin: '-1px',
            }),
        }}
      >
        {children}
      </CardContent>
    </Box>
  );
});

CardWrapper.displayName = 'CardWrapper';
